import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { errorMessage } from "@/api/errors";
import { useAuthStore } from "@/stores/auth";

export interface PickedUser {
  id: string;
  email: string;
  displayName: string | null;
  organizationId: string | null;
  role: string | null;
}

interface Props {
  /** Mode controls UX: PERMANENT pulls a same-org dropdown; TEMPORARY allows cross-org by email lookup. */
  mode: "PERMANENT" | "TEMPORARY";
  /** For PERMANENT, restrict to this org (defaults to current user's org). */
  organizationId?: string;
  value: PickedUser | null;
  onChange: (u: PickedUser | null) => void;
  /** Exclude these user IDs from the dropdown (e.g. already granted). */
  excludeIds?: string[];
}

export default function UserPicker({ mode, organizationId, value, onChange, excludeIds }: Props) {
  const me = useAuthStore((s) => s.user)!;
  const targetOrgId = organizationId ?? me.organizationId ?? undefined;

  if (mode === "PERMANENT") {
    return <SameOrgPicker orgId={targetOrgId} value={value} onChange={onChange} excludeIds={excludeIds} />;
  }
  return <EmailLookup value={value} onChange={onChange} />;
}

function SameOrgPicker({
  orgId, value, onChange, excludeIds,
}: { orgId?: string; value: PickedUser | null; onChange: (u: PickedUser | null) => void; excludeIds?: string[] }) {
  const [q, setQ] = useState("");

  const users = useQuery({
    queryKey: ["users", orgId, q],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (orgId) params.organizationId = orgId;
      if (q) params.q = q;
      const { data, error, response } = await api.GET("/api/v1/users", {
        params: { query: params },
      });
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as PickedUser[];
    },
    enabled: !!orgId,
  });

  const excluded = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);
  const candidates = (users.data ?? []).filter((u) => !excluded.has(u.id));

  return (
    <div className="space-y-2">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="按邮箱搜索本公司用户…"
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
      <div className="max-h-48 overflow-auto rounded-md border border-border bg-background divide-y divide-border">
        {users.isLoading && (
          <div className="px-3 py-2 text-sm text-muted-foreground">加载中…</div>
        )}
        {!users.isLoading && candidates.length === 0 && (
          <div className="px-3 py-2 text-sm text-muted-foreground">未找到用户</div>
        )}
        {candidates.map((u) => {
          const selected = value?.id === u.id;
          return (
            <button
              key={u.id}
              type="button"
              onClick={() => onChange(u)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted ${selected ? "bg-primary/10" : ""}`}
            >
              <div className="font-medium">{u.email}</div>
              {(u.displayName || u.role) && (
                <div className="text-xs text-muted-foreground">
                  {u.displayName} {u.role && <span className="ml-1">· {u.role}</span>}
                </div>
              )}
            </button>
          );
        })}
      </div>
      {value && (
        <div className="text-xs text-muted-foreground">
          已选：<span className="font-medium text-foreground">{value.email}</span>
          <button type="button" onClick={() => onChange(null)} className="ml-2 text-destructive hover:underline">
            清除
          </button>
        </div>
      )}
    </div>
  );
}

function EmailLookup({
  value, onChange,
}: { value: PickedUser | null; onChange: (u: PickedUser | null) => void }) {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function doLookup() {
    setErr(null);
    setBusy(true);
    try {
      const { data, error, response } = await api.GET("/api/v1/users/lookup", {
        params: { query: { email } },
      });
      if (error || !response.ok) {
        setErr(errorMessage(error || response, "未找到该用户"));
        onChange(null);
        return;
      }
      onChange(data as unknown as PickedUser);
    } catch (e) {
      setErr(errorMessage(e, "查询失败"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="输入跨公司用户的邮箱"
          className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={doLookup}
          disabled={busy || !email}
          className="rounded-md border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
        >
          {busy ? "查询中..." : "查询"}
        </button>
      </div>
      {err && <div className="text-xs text-destructive">{err}</div>}
      {value && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
          <div className="font-medium">{value.email}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {value.displayName ?? "(未设置显示名)"} · {value.role ?? "无角色"} ·
            <span className="ml-1">org: {value.organizationId?.slice(0, 8) ?? "—"}</span>
          </div>
          <button
            type="button"
            onClick={() => { onChange(null); setEmail(""); }}
            className="mt-1 text-xs text-destructive hover:underline"
          >
            重选
          </button>
        </div>
      )}
    </div>
  );
}
